<template>
  <v-app>
    <LargeImgModal />

    <v-navigation-drawer
      v-model="showMobileMenu"
      app
      absolute
      temporary
      :style="epaDrawerStyle"
      style="z-index: 15"
    >
      <v-list
        dense
        nav
      >
        <v-list-item
          v-for="item in visibleNavigationItems"
          :key="item.to"
          :to="item.to"
          @click="showMobileMenu = false"
          class="text-decoration-none"
          active-class="text-primary"
        >
          <v-list-item-icon>
            <v-icon>{{ item.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ item.label }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>

    <div class="d-flex flex-column vh-100">
      <EpaHeader
        ref="headerComponent"
        v-if="showEpaShell"
        style="z-index: 20"
      />

      <v-app-bar
        app
        :dense="isMiddleOrDownScreen"
        color="white"
        :elevation="1"
        style="
          display: flex;
          flex-grow: 0;
          position: relative;
          width: 100%;
          z-index: 10;
          overflow-x: auto;
          overflow-y: hidden;
        "
      >
        <div
          class="w-100 d-flex flex-row justify-content-between align-items-center"
          style="gap: 0.5rem; z-index: 10; flex-grow: 1"
        >
          <div
            class="d-flex"
            :class="!isMiddleOrDownScreen ? 'flex-column' : 'flex-row align-items-baseline'"
            style="column-gap: 0.5rem"
          >
            <div
              class="d-flex flex-row align-items-center"
              :style="{ gap: isMobile ? '0.25rem' : '0.5rem' }"
            >
              <router-link to="/">
                <div :class="{ small: isMobile }">
                  {{ VERSION.title }}
                </div>
              </router-link>
              <v-menu
                v-if="isMobile"
                offset-y
              >
                <template #activator="{ on, attrs }">
                  <v-btn
                    icon
                    x-small
                    v-bind="attrs"
                    v-on="on"
                    aria-label="Show Build Information"
                  >
                    <v-icon small>mdi-information-outline</v-icon>
                  </v-btn>
                </template>
                <v-card class="pa-2">
                  <div class="small">Build: {{ formatDate(VERSION.compiled) }}</div>
                </v-card>
              </v-menu>

              <v-btn
                v-if="isMobile"
                :aria-label="showEpaShell ? 'Hide EPA header/footer' : 'Show EPA header/footer'"
                @click="showEpaShell = !showEpaShell"
                depressed
                text
                class="flat-button"
                style="height: 24px"
              >
                <span
                  class="text-muted"
                  style="font-size: smaller"
                >
                  EPA
                </span>
                <v-icon small>
                  {{ showEpaShell ? 'mdi-unfold-less-horizontal' : 'mdi-unfold-more-horizontal' }}
                </v-icon>
              </v-btn>
            </div>

            <div
              v-if="!isMobile"
              class="d-flex flex-row align-items-baseline"
              style="gap: 1rem"
            >
              <div
                class="text-muted"
                style="font-size: smaller"
              >
                Build: {{ formatDate(VERSION.compiled) }}
              </div>
              <v-btn
                :aria-label="showEpaShell ? 'Hide EPA header/footer' : 'Show EPA header/footer'"
                @click="showEpaShell = !showEpaShell"
                depressed
                text
                class="flat-button"
                style="height: 24px"
              >
                <span
                  class="text-muted"
                  style="font-size: smaller"
                >
                  EPA
                </span>
                <v-icon small>
                  {{ showEpaShell ? 'mdi-unfold-less-horizontal' : 'mdi-unfold-more-horizontal' }}
                </v-icon>
              </v-btn>
            </div>
          </div>

          <div
            class="d-flex flex-row align-items-center"
            style="gap: 0.5rem"
          >
            <template v-if="!isMiddleOrDownScreen">
              <v-btn
                v-for="item in visibleNavigationItems"
                :key="item.to"
                :small="isLargeOrDownScreen"
                active-class="text-primary"
                dense
                :class="isLargeOrDownScreen ? 'px-1' : 'px-2'"
                class="-no-link-under text-decoration-none"
                :to="item.to"
                :aria-label="`Navigate to ${item.label}`"
                text
              >
                <v-icon
                  small
                  :class="{ 'mr-1': isLargeOrDownScreen }"
                  :style="isLargeOrDownScreen ? 'margin-right: 0.15rem' : ''"
                >
                  {{ item.icon }}
                </v-icon>
                <span class="small">{{ item.label }}</span>
              </v-btn>
            </template>
            <v-tooltip
              bottom
              open-on-hover
              v-bind="defaultVTooltipProps"
              :disabled="!isChemCartDisabled"
            >
              <template #activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                <div
                  v-bind="tooltipAttrs"
                  v-on="tooltipOn"
                >
                  <ChemCartButton :disabled="isChemCartDisabled" />
                </div>
              </template>
              ChemCart is disabled in AMOS and CheT modules
            </v-tooltip>
            <v-app-bar-nav-icon
              aria-label="Open navigation menu"
              v-show="isMiddleOrDownScreen"
              @click.stop="showMobileMenu = !showMobileMenu"
            />
            <!-- </v-btn-toggle> -->
            <!-- Disabled till better times and proper OAuth implementation
            <template v-if="feature('auth')">
              <LoginDialog
                v-if="!isAuthenticated"
                v-model="showLogin"
                button-title="mdi-account-arrow-left-outline"
                max-width="300"
                title="Log In"
              />
              <LoggedInStatus v-else />
            </template>
      -->
          </div>
        </div>
      </v-app-bar>

      <v-main class="pt-0">
        <div class="d-flex flex-column h-100">
          <v-expand-transition>
            <v-card
              v-show="showChemCart"
              class="mt-1 mx-1"
              max-height="40vh"
              style="z-index: 5"
            >
              <v-card-text class="py-1 h-100">
                <ChemCart />
              </v-card-text>
            </v-card>
          </v-expand-transition>
          <div
            class="overflow-auto"
            style="flex-basis: 0; flex-grow: 1"
          >
            <router-view />
          </div>
        </div>
      </v-main>

      <v-footer
        :app="!showEpaShell"
        color="primary"
        :fixed="!showEpaShell"
        light
      >
        <div class="w-100 footer-grid align-items-center gap-1">
          <div />

          <div class="d-flex gap-1">
            <v-btn
              v-for="link in config.links"
              dark
              x-small
              text
              depressed
              :key="link.icon"
              :ripple="false"
              v-b-tooltip
              :to="link.to"
              :href="link.url"
              :target="link.target"
              :title="link.title"
              class="text-transform-none"
              :aria-label="link.title"
            >
              <span v-if="!isMobile">{{ link.label }}</span>
              <v-icon
                v-else
                small
              >
                {{ link.icon }}
              </v-icon>
            </v-btn>

            <v-menu
              offset-y
              top
              max-width="15rem"
              max-height="15rem"
            >
              <template #activator="{ on, attrs }">
                <v-btn
                  x-small
                  text
                  depressed
                  :ripple="false"
                  dark
                  v-bind="attrs"
                  v-on="on"
                  class="text-transform-none"
                  aria-label="Disclaimer"
                >
                  <span
                    v-if="!isMobile"
                    class="underline-dashed"
                  >
                    Disclaimer
                  </span>
                  <v-icon
                    v-else
                    small
                  >
                    mdi-information-outline
                  </v-icon>
                </v-btn>
              </template>

              <v-card class="pa-3">
                {{ config.disclaimer }}
              </v-card>
            </v-menu>
          </div>

          <div class="d-flex justify-content-end">
            <FeedbackDialog />
          </div>
        </div>
      </v-footer>
    </div>

    <EpaFooter v-if="showEpaShell" />
  </v-app>
</template>

<script>
import { ref } from 'vue'
import LargeImgModal from './components/dialogs/LargeImgModal.vue'
import FeedbackDialog from '@/components/feedback/FeedbackDialog.vue'
// import LoginDialog from '@/components/auth/LoginDialog'
// import LoggedInStatus from '@/components/auth/LoggedInStatus'
import AuthBase from '@/components/auth/AuthBase'
import FeaturesBase from '@/components/FeaturesBase'
import { formatDate } from '@science-data-experts/formatters'
import ChemCartButton from '@/components/ChemCart/ChemCartButton.vue'
// import AddChemicals from '@/components/ChemCart/AddChemicals.vue'
import ChemCart from '@/components/ChemCart/ChemCart.vue'
import EpaHeader from '@/components/epa/EpaHeader.vue'
import EpaFooter from '@/components/epa/EpaFooter.vue'
import { defaultVTooltipProps } from '@/utils/defaultProps'
import { useElementSize } from '@vueuse/core'
import version from '../public/version.json'

const navigationItems = [
  {
    featureClause: ['search'],
    icon: 'mdi-database-search',
    label: 'Search',
    to: '/search',
  },
  {
    featureClause: ['hazard'],
    icon: 'mdi-biohazard',
    label: 'Hazard',
    to: '/hazard',
  },
  {
    featureClause: ['safety'],
    icon: 'mdi-safety-goggles',
    label: 'Safety',
    to: '/safety',
  },
  {
    icon: 'mdi-calculator',
    label: 'Predict',
    to: '/combined-predict',
  },
  {
    featureClause: ['alerts'],
    icon: 'mdi-alert-rhombus-outline',
    label: 'Alerts',
    to: '/alerts',
  },
  {
    featureClause: ['toxprints'],
    icon: 'mdi-fingerprint',
    label: 'Fingerprints',
    to: '/toxprints',
  },
  {
    featureClause: ['stdizer'],
    icon: 'mdi-laravel',
    label: 'Standardize',
    to: '/stdizer',
  },
  {
    featureClause: ['resolver'],
    icon: 'mdi-tools',
    label: 'Utilities',
    to: '/utils',
  },
  {
    featureClause: ['amos'],
    icon: 'mdi-hexagon-multiple-outline',
    label: 'AMOS',
    to: '/amosapp',
  },
  {
    featureClause: ['chet'],
    icon: 'mdi-react',
    label: 'CheT',
    to: '/chetapp',
  },
]

export default {
  mixins: [AuthBase, FeaturesBase],

  setup() {
    const headerComponent = ref(null)
    const { width, height } = useElementSize(headerComponent)

    return {
      headerComponent,
      width,
      height,
    }
  },

  components: {
    LargeImgModal,
    FeedbackDialog,
    EpaHeader,
    EpaFooter,
    // LoginDialog,
    // LoggedInStatus,
    ChemCartButton,
    // AddChemicals,
    ChemCart,
  },

  data: () => ({
    showLogin: false,
    showMobileMenu: false,
    showEpaShell: false,
    polling: null,
    epaHeaderHeightFallback: 148,
    VERSION: version,
    config: {
      disclaimer:
        'The data are not reviewed by USEPA – the user must apply judgment in use of the information. The results do not indicate EPA’s position on the use or regulation of these chemicals.',
      links: [
        {
          icon: 'mdi-home',
          title: 'Home',
          label: 'Home',
          to: '/',
        },
        {
          icon: 'mdi-monitor-dashboard',
          title: 'Cheminformatics Modules and API details',
          label: 'Modules/API',
          to: '/status',
        },
        {
          icon: 'mdi-format-list-bulleted-type',
          title: 'Release Notes',
          label: 'Release Notes',
          to: '/release_notes',
        },
        {
          icon: 'mdi-multimedia',
          label: 'Manuals and Multimedia',
          title: 'Cheminformatics Modules Manuals and Multimedia',
          to: '/media',
        },
      ],
      logo: 'sde-logo.png',
      title: 'Cheminformatics Modules',
      version: 'DEV',
    },
    navigationItems,
    formatDate,
    chemCartDisabledPaths: ['/amosapp', '/chetapp'],
    defaultVTooltipProps,
  }),

  computed: {
    showChemCart() {
      return this.$store.getters.isViewStructures
    },

    isMobile() {
      return this.$vuetify.breakpoint.xs
    },

    isMiddleOrDownScreen() {
      return this.$vuetify.breakpoint.mdAndDown
    },

    isLargeOrDownScreen() {
      return this.$vuetify.breakpoint.lgAndDown
    },

    epaHeaderHeight() {
      return this.height || this.epaHeaderHeightFallback
    },

    epaAppBarStyle() {
      return this.showEpaShell ? { top: `${this.epaHeaderHeight}px` } : null
    },

    epaDrawerStyle() {
      return this.showEpaShell
        ? {
            top: `${this.epaHeaderHeight}px`,
            height: `calc(100% - ${this.epaHeaderHeight}px)`,
          }
        : null
    },

    visibleNavigationItems() {
      return this.navigationItems.filter((item) => this.isNavigationItemVisible(item))
    },
    isChemCartDisabled() {
      return this.chemCartDisabledPaths.includes(this.$route.path)
    },
  },

  watch: {
    'isMiddleOrDownScreen'(newVal) {
      if (!newVal) {
        this.showMobileMenu = false
      }
    },
    '$route.path'(newPath) {
      if (newPath === '/') {
        this.$store.commit('SET_VIEW_STRUCTURES', false)
      }
    },
  },

  methods: {
    isNavigationItemVisible(item) {
      return !item.featureClause?.length ? true : item.featureClause.some((featureName) => this.feature(featureName))
    },

    startPolling() {
      this.polling = setInterval(() => {
        this.$store.dispatch('updateModuleInfo')
      }, 120000)
    },
  },

  created() {
    this.$store.commit('SET_VIEW_STRUCTURES', false)

    // Don't do, very annoying
    // this.startPolling()
  },

  beforeDestroy() {
    clearInterval(this.polling)
  },

  beforeCreate() {
    this.$store.dispatch('initMetadata')
  },
}
</script>

<style lang="scss">
header .v-toolbar__content {
  flex-grow: 1;
}

.no-link-under {
  &:hover {
    text-decoration: none;
  }
}

// body {
//   margin: 0;
//   padding: 0;
// }

.tabs-wrapper {
  width: 100%;
}

.modal-row {
  .modal-body {
    padding: 0;
  }

  .close {
    height: 52px;
    width: 46px;
  }

  .s-body {
    height: 347px;
  }

  .p-body {
    margin-left: -1.25rem;
    margin-right: -1.25rem;
    padding-left: 1.25rem;
    padding-right: 1.25rem;
    height: 300px;
    overflow-y: auto;
  }
}

td {
  position: relative;
}

.fa-spin {
  animation: fa-spin 2s infinite linear;
  font-size: 96px;
}

.large-img {
  height: 498px;
  width: 100%;
}

.disclaimer {
  color: yellow;
  font-size: smaller;
  text-align: justify;
}

.table.table-records {
  width: 100%;
  overflow-x: auto;

  @media only screen and (max-width: 1260px) {
    display: block;
  }

  & tr {
    & th {
      padding: 2pt;
      text-align: center;
      // font-size: 13px;
      font-weight: normal;
      overflow: hidden;
      vertical-align: center;
    }

    & th.rotate {
      height: 10em;
      white-space: nowrap;
      vertical-align: bottom;
      text-align: -webkit-center;

      & > span {
        display: block;
        transform: rotate(-90deg);
        width: 44px;
        padding-left: 20px;
      }
    }

    & td {
      padding: 0;
      text-align: center;
      vertical-align: middle;

      span {
        padding: 4px;
      }

      & .active {
        box-shadow: inset 0 0 0 2px rgba(0, 0, 0, 1);
      }

      & .nowrap {
        white-space: nowrap;
      }
    }
  }
}

.table-td-hover {
  cursor: pointer;

  &:hover {
    background: rgba(0, 0, 0, 0.3);
  }
}

.hazard-name {
  font-weight: bold;
  font-style: italic;
}

.chemical-name {
  font-weight: bold;
  font-style: italic;
}

.table-orange {
  background: rgba(#ff8c00, 0.4);
}

.authoritative {
  font-weight: bold;
}

.screening {
  font-style: normal;
}

.experimental {
  font-style: normal;

  text-decoration: underline;
}

.predicted {
  font-style: italic;
}

.right {
  float: right;
}

.row-pseudo-title {
  text-align: right !important;
  font-style: italic;
  padding: 1em !important;
}

.shadered-on-hover {
  margin: 2px;
  cursor: pointer;
  border: 1px solid transparent;

  &:hover {
    border: 1px darkgray solid;
    box-shadow: 3px 3px 3px #666;
    background-color: white;
  }
}

.bordered-on-hover {
  margin: 2px;
  cursor: pointer;
  border: 1px solid transparent;

  &:hover {
    border: 1px darkgray solid;
  }
}

.shadowed-on-hover {
  margin: 2px;

  &:hover {
    box-shadow: 3px 3px 3px #666;
  }
}

.shadowed {
  box-shadow: 3px 3px 3px #666;
}

.bordered {
  border: 1px darkgray solid;
}

.shadered {
  border: 1px darkgray solid;
  box-shadow: 3px 3px 3px #666;
  background-color: white;
}

.v-data-table {
  tbody tr:hover:not(.v-data-table__expanded__content) {
    background: #fafafa !important;
  }
}

.top-right {
  font-size: smaller !important;
  position: absolute !important;
  top: 1px !important;
  right: 1px !important;
  padding: 2px !important;
}

.rotate--90 {
  /* FF3.5+ */
  -moz-transform: rotate(-90deg);
  /* Opera 10.5 */
  -o-transform: rotate(-90deg);
  /* Saf3.1+, Chrome */
  -webkit-transform: rotate(-90deg);
  /* IE6,IE7 */
  filter: progid:DXImageTransform.Microsoft.BasicImage(rotation=0.083);
  /* IE8 */
  -ms-filter: 'progid:DXImageTransform.Microsoft.BasicImage(rotation=0.083)';
  /* Standard */
  transform: rotate(-90deg);
}

.text-truncate-150 {
  max-width: 150px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

footer div.text-center {
  margin-left: auto;
  margin-right: auto;
}
</style>

<style scoped lang="scss">
.color-light-grey {
  color: lightgrey;
}

.v-toolbar__content {
  padding: 0px !important;
}

.underline-dashed:hover {
  text-decoration: underline dashed;
  text-underline-offset: 2px;
}

.text-transform-none {
  text-transform: none !important;
}

.footer-grid {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
}
</style>
